import { useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Market } from '../dataProvider/types';
import { ErasureRow } from './ErasureRow';
import { RequestErasureAction } from './RequestErasureAction';
import { useErasureQueue } from './useErasureQueue';
import type { ErasureRow as Row } from './erasureQueueModel';

/**
 * The erasure (right-to-be-forgotten) worklist (D-erasure, Phase 7 surface 6, the last and sharpest).
 * A compliance worklist over the erasureRequests collection. The surface CENTERS on the
 * disposition-plan PREVIEW (the blast radius shown before the irreversible execute), reachable on
 * every row.
 *
 * Trust is SPLIT (the maker-checker): request / cancel / reject sit at COMPLIANCE; the irreversible
 * EXECUTE is super_admin-only AND gated on the grace window having elapsed. The lifecycle is
 * requested -> (grace window, derived off the injected clock, cancellable) -> executed | cancelled |
 * rejected. OVERSIGHT-GLOBAL with a region facet. ROLE-gated to compliance and super_admin.
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
  now,
}: {
  title: string;
  rows: Row[];
  emptyText: string;
  testId: string;
  now: number;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <ErasureRow key={row.id} row={row} now={now} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf` (the clock the grace
 *  derivation and the execute timestamps are measured against). */
export const ErasureQueueView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [regionFilter, setRegionFilter] = useState<Market | null>(null);
  const { view, isLoading } = useErasureQueue(now, regionFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Compliance
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Erasure requests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Right-to-be-forgotten requests. Each request waits through a grace
        window before it can be executed, and during that window it can be
        cancelled. Execute is irreversible and reserved to a second hand. View
        the disposition plan to see exactly what erasure would do to the member,
        record by record, before executing. The audit trail is never erased, and
        records under a tax, AML or financial obligation are anonymised and
        retained. The view spans both markets by default.
      </Typography>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="erasure-region-label">Region</InputLabel>
          <Select
            labelId="erasure-region-label"
            label="Region"
            value={regionFilter ?? 'all'}
            inputProps={{ 'data-testid': 'erasure-region' }}
            onChange={(e) =>
              setRegionFilter(
                e.target.value === 'all' ? null : (e.target.value as Market),
              )
            }
          >
            <MenuItem value="all">All markets</MenuItem>
            <MenuItem value="uk">UK</MenuItem>
            <MenuItem value="us">US</MenuItem>
          </Select>
        </FormControl>
        <RequestErasureAction now={now} />
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Pending"
            rows={view.pending}
            emptyText="No erasure requests awaiting action in scope."
            testId="erasure-section-pending"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Resolved"
            rows={view.resolved}
            emptyText="No resolved erasure requests in scope."
            testId="erasure-section-resolved"
            now={now}
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /erasure. ROLE-gated to compliance and super_admin: a viewer's
 * read-everywhere '*:read' wildcard would slip a capability read-gate, so the explicit role gate holds
 * the boundary. The irreversible EXECUTE is additionally capability-gated to super_admin
 * (erasure:execute), the maker-checker within the surface (D-erasure).
 */
export const ErasureQueue = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <ErasureQueueView />
  </RequireAccess>
);
